import { completeWinningPage08Schema } from "@/lib/winningPage08Schema";
import { completeWinningPage01Graph } from "@/lib/winningPage01Schema";
export type SchemaNode = Record<string, unknown>;
export type SchemaGraph = {
  "@context"?: string;
  "@graph"?: SchemaNode[];
};

export const SCHEMA_ENTITY_IDS = {
  organization: "https://integrated.social/#organization",
  website: "https://integrated.social/#website",
  person: "https://integrated.social/modi-elnadi/#person",
} as const;

export const SCHEMA_ASSETS = {
  founderPortrait: "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
  logo: "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
  socialPreview: "https://integrated.social/media/integrated-social-default-social-preview_94dfe470.jpg",
} as const;

const LEGACY_SCHEMA_ASSETS: Record<string, string> = {
  "https://integrated.social/media/modi-elnadi-ai-growth-director-london-portrait_cd4a219d.jpg": SCHEMA_ASSETS.founderPortrait,
  "https://integrated.social/media/original-blue-logo_264ff571.webp": SCHEMA_ASSETS.logo,
  "https://integrated.social/media/og-social-preview-1200x630-final_a70944b1.jpg": SCHEMA_ASSETS.socialPreview,
};

const COMPANY_LINKEDIN = "https://www.linkedin.com/company/integrated-social-ltd";
const PERSON_LINKEDIN = "https://www.linkedin.com/in/modielnadi/";
const PERSON_X = "https://x.com/MoElnadi";

function isEntityType(node: SchemaNode, entityType: string) {
  const type = node["@type"];
  return type === entityType || (Array.isArray(type) && type.includes(entityType));
}

function normalizeValue(value: unknown): unknown {
  if (typeof value === "string") return LEGACY_SCHEMA_ASSETS[value] ?? value;
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as SchemaNode).map(([key, nested]) => [key, normalizeValue(nested)]),
    );
  }
  return value;
}

function normalizeKnownEntity(node: SchemaNode): SchemaNode {
  const normalized = normalizeValue(node) as SchemaNode;
  const id = normalized["@id"];

  if (id === SCHEMA_ENTITY_IDS.organization || isEntityType(normalized, "Organization")) {
    return {
      ...normalized,
      "@id": SCHEMA_ENTITY_IDS.organization,
      name: "Integrated.Social",
      legalName: "Integrated Social Ltd",
      url: "https://integrated.social/",
      logo: { "@type": "ImageObject", url: SCHEMA_ASSETS.logo, width: 457, height: 457 },
      sameAs: [COMPANY_LINKEDIN],
    };
  }

  if (id === SCHEMA_ENTITY_IDS.person || isEntityType(normalized, "Person")) {
    return {
      ...normalized,
      "@id": SCHEMA_ENTITY_IDS.person,
      name: "Modi Elnadi",
      url: "https://integrated.social/modi-elnadi/",
      image: SCHEMA_ASSETS.founderPortrait,
      sameAs: [PERSON_LINKEDIN, PERSON_X],
      worksFor: { "@id": SCHEMA_ENTITY_IDS.organization },
    };
  }

  if (id === SCHEMA_ENTITY_IDS.website || isEntityType(normalized, "WebSite")) {
    return {
      ...normalized,
      "@id": SCHEMA_ENTITY_IDS.website,
      url: "https://integrated.social/",
      name: "Integrated.Social",
      publisher: { "@id": SCHEMA_ENTITY_IDS.organization },
      inLanguage: "en-GB",
    };
  }

  return normalized;
}

export const canonicalPersonEntity = (): SchemaNode => ({
  "@type": "Person",
  "@id": SCHEMA_ENTITY_IDS.person,
  name: "Modi Elnadi",
  jobTitle: "Director of AI Growth and AEO",
  description: "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
  url: "https://integrated.social/modi-elnadi/",
  image: SCHEMA_ASSETS.founderPortrait,
  worksFor: { "@id": SCHEMA_ENTITY_IDS.organization },
  sameAs: [PERSON_LINKEDIN, PERSON_X],
  knowsAbout: [
    "Paid and earned marketing",
    "Personas",
    "SEO",
    "Answer Engine Optimization",
    "Generative Engine Optimization",
    "LLM optimization",
    "Agentic AI with human review",
    "Google Ads",
    "Performance Max",
    "Account-based marketing",
    "CRM",
    "Conversion rate optimisation",
    "EMEA marketing",
    "Subscription growth",
    "Fintech marketing",
    "eCommerce",
  ],
  skills: [
    "Paid and earned, planned together",
    "Personas and buying committees",
    "SEO, AEO, GEO and LLMO",
    "Agentic AI with human review",
    "Google Ads and Performance Max",
    "Paid social and LinkedIn",
    "CRM and lifecycle",
    "CRO and landing pages",
    "Account-based marketing",
    "Multi-market EMEA",
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Professional Experience",
      name: "20+ years in digital marketing",
      description: "Senior marketing work across fintech, SaaS, professional services, and technology sectors.",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Specialisation",
      name: "Agentic AI for B2B Growth",
      description: "Practitioner of agentic AI systems for bounded marketing workflows with human review.",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Specialisation",
      name: "Answer Engine Optimisation (AEO) and GEO",
      description: "Practitioner of answer-engine and generative-engine optimisation for B2B marketing.",
    },
  ],
});

const frozenLocalPersonEntity = (): SchemaNode => ({
  "@type": "Person",
  "@id": SCHEMA_ENTITY_IDS.person,
  name: "Modi Elnadi",
  jobTitle: "Founder",
  description: "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
  url: "https://integrated.social/modi-elnadi/",
  image: SCHEMA_ASSETS.founderPortrait,
  worksFor: { "@id": SCHEMA_ENTITY_IDS.organization },
  sameAs: [PERSON_LINKEDIN, PERSON_X],
  knowsAbout: [
    "Paid and earned marketing",
    "Personas",
    "SEO",
    "Answer Engine Optimization",
    "Generative Engine Optimization",
    "LLM optimization",
    "Agentic AI with human review",
    "Google Ads",
    "Performance Max",
    "Account-based marketing",
    "CRM",
    "Conversion rate optimisation",
    "EMEA marketing",
    "Subscription growth",
    "Fintech marketing",
    "eCommerce",
  ],
  skills: [
    "Paid and earned, planned together",
    "Personas and buying committees",
    "SEO, AEO, GEO and LLMO",
    "Agentic AI with human review",
    "Google Ads and Performance Max",
    "Paid social and LinkedIn",
    "CRM and lifecycle",
    "CRO and landing pages",
    "Account-based marketing",
    "Multi-market EMEA",
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Professional Experience",
      name: "16+ years B2B marketing leadership",
      description: "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA.",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Specialisation",
      name: "Agentic AI for B2B Growth",
      description: "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023.",
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Specialisation",
      name: "Answer Engine Optimisation (AEO) and GEO",
      description: "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode.",
    },
  ],
});

export const coreSchemaEntities = (route?: string): SchemaNode[] => [
  {
    "@type": ["Organization", "ProfessionalService"],
    "@id": SCHEMA_ENTITY_IDS.organization,
    name: "Integrated.Social",
    legalName: "Integrated Social Ltd",
    url: "https://integrated.social/",
    logo: { "@type": "ImageObject", url: SCHEMA_ASSETS.logo, width: 457, height: 457 },
    knowsAbout: ["Local AI search marketing", "Google Business Profile optimisation", "Local SEO for independent businesses"],
    sameAs: [COMPANY_LINKEDIN],
  },
  route && /^\/local(?:\/|$)/.test(route) ? frozenLocalPersonEntity() : canonicalPersonEntity(),
  {
    "@type": "WebSite",
    "@id": SCHEMA_ENTITY_IDS.website,
    url: "https://integrated.social/",
    name: "Integrated.Social",
    publisher: { "@id": SCHEMA_ENTITY_IDS.organization },
    inLanguage: "en-GB",
  },
];

/**
 * Makes each rendered JSON-LD graph self-contained. This prevents references to
 * the site's shared organization, website and founder IDs from becoming dangling
 * when a crawler fetches an individual route without first crawling the homepage.
 */
function mergeSchemaValues(existing: unknown, incoming: unknown, key: string): unknown {
  if (existing === undefined) return incoming;
  if (incoming === undefined) return existing;

  if (key === "@type") {
    const values = Array.from(new Set([
      ...(Array.isArray(existing) ? existing : [existing]),
      ...(Array.isArray(incoming) ? incoming : [incoming]),
    ]));
    return values.length === 1 ? values[0] : values;
  }

  if (Array.isArray(existing) && Array.isArray(incoming)) {
    const seen = new Set<string>();
    return [...existing, ...incoming].filter((value) => {
      const signature = JSON.stringify(value);
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    });
  }

  if (
    existing && incoming &&
    typeof existing === "object" && !Array.isArray(existing) &&
    typeof incoming === "object" && !Array.isArray(incoming)
  ) {
    const merged: SchemaNode = { ...(existing as SchemaNode) };
    for (const [nestedKey, nestedValue] of Object.entries(incoming as SchemaNode)) {
      merged[nestedKey] = mergeSchemaValues(merged[nestedKey], nestedValue, nestedKey);
    }
    return merged;
  }

  // The first full declaration is authoritative; later duplicate declarations can
  // contribute only missing fields. This avoids conflicting values for one @id.
  return existing;
}

function deduplicateSchemaNodes(nodes: SchemaNode[]): SchemaNode[] {
  const graph: SchemaNode[] = [];
  const positions = new Map<string, number>();

  for (const node of nodes.map(normalizeKnownEntity)) {
    const id = node["@id"];
    if (typeof id !== "string") {
      graph.push(node);
      continue;
    }

    const position = positions.get(id);
    if (position === undefined) {
      positions.set(id, graph.length);
      graph.push(node);
      continue;
    }

    const existing = graph[position];
    const merged: SchemaNode = { ...existing };
    for (const [key, value] of Object.entries(node)) {
      merged[key] = mergeSchemaValues(merged[key], value, key);
    }
    graph[position] = normalizeKnownEntity(merged);
  }

  return graph;
}

function isBreadcrumbList(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const type = (value as SchemaNode)["@type"];
  return type === "BreadcrumbList" || (Array.isArray(type) && type.includes("BreadcrumbList"));
}

/**
 * Legacy routes express breadcrumb data in both supported Schema.org shapes:
 * as a standalone node and nested below WebPage.breadcrumb. A single graph
 * must retain the list exactly once, while WebPage can reference that node.
 */
function normalizeBreadcrumbNodes(nodes: SchemaNode[], fallbackId?: string): SchemaNode[] {
  const promoted: SchemaNode[] = [];
  const normalized = nodes.map((node) => {
    if (isBreadcrumbList(node)) {
      if (typeof node["@id"] === "string" || !fallbackId) return node;
      return { ...node, "@id": fallbackId };
    }

    const breadcrumbValue = node.breadcrumb;
    if (!isBreadcrumbList(breadcrumbValue)) return node;
    const breadcrumb = breadcrumbValue as SchemaNode;

    const id = typeof breadcrumb["@id"] === "string" ? breadcrumb["@id"] : fallbackId;
    promoted.push({ ...breadcrumb, ...(id ? { "@id": id } : {}) });
    return { ...node, breadcrumb: id ? { "@id": id } : breadcrumb };
  });
  return [...normalized, ...promoted];
}

export function completeSchemaGraph(schema: SchemaGraph, route?: string): SchemaGraph {
  if (!Array.isArray(schema["@graph"])) return normalizeValue(schema) as SchemaGraph;

  const graph = deduplicateSchemaNodes(schema["@graph"]);
  for (const entity of coreSchemaEntities(route)) {
    const id = entity["@id"] as string;
    const position = graph.findIndex((node) => node["@id"] === id);
    if (position === -1) {
      graph.push(entity);
      continue;
    }

    const merged: SchemaNode = { ...graph[position] };
    for (const [key, value] of Object.entries(entity)) {
      merged[key] = mergeSchemaValues(merged[key], value, key);
    }
    graph[position] = normalizeKnownEntity(merged);
  }

  const completedGraph = deduplicateSchemaNodes(graph);
  // Explicitly opted-in article graphs declare their actual language. Legacy and local routes retain their existing contract.
  const hasUsEnglishArticle = route?.startsWith("/blog/") && completedGraph.some((node) =>
    ["Article", "BlogPosting", "NewsArticle"].includes(String(node["@type"])) && node.inLanguage === "en-US");
  if (route === "/services/google-ai-mode-optimisation/" || route === "/services/seo-aeo-geo/") {
    // This release localizes inherited root labels only on this opted-in route.
    // Preserve frozen-local and every other route's declarations and identities.
    for (let index = 0; index < completedGraph.length; index++) {
      if (!Object.values(SCHEMA_ENTITY_IDS).includes(completedGraph[index]["@id"] as typeof SCHEMA_ENTITY_IDS[keyof typeof SCHEMA_ENTITY_IDS])) continue;
      completedGraph[index] = JSON.parse(JSON.stringify(completedGraph[index], (key, value) => {
        // Localize text values, never JSON keys or canonical/reference strings.
        if (typeof value !== "string" || ["@id", "url", "sameAs", "item", "contentUrl", "embedUrl"].includes(key) || /^(?:https?:|\/|#)/.test(value)) return value;
        const localized = value.replace(/\boptimisation\b/g, "optimization")
          .replace(/\bOptimisation\b/g, "Optimization")
          .replace(/\bSpecialisation\b/g, "Specialization");
        return route !== "/services/seo-aeo-geo/" ? localized : localized.replace(/\bprogrammes\b/g, "programs")
          .replace(/\bprogramme\b/g, "program")
          .replace(/\bspecialising\b/g, "specializing");
      }));
    }
  }
  if (route === "/services/geo-agency-london/" || route === "/services/gemini-agentic-ai/" || route === "/services/ai-governance/" || route === "/services/ppc-performance-max/" || route === "/services/performance-max-agency/" || route === "/services/google-ai-mode-optimisation/" || route === "/services/seo-aeo-geo/" || route === "/tools/ai-token-calculator/" || hasUsEnglishArticle) {
    const website = completedGraph.find((node) => node["@id"] === SCHEMA_ENTITY_IDS.website);
    if (website) website.inLanguage = "en-US";
  }
  // Complete this source-qualified news analysis in both SSR and runtime producers.
  if (route === "/blog/chatgpt-search-20-percent-google-query-volume/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 1600, height: 900 };
      }
      delete article.speakable;
      delete article.wordCount; // The inherited content counter excludes rendered visuals and FAQs.
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Complete only the source-qualified Nano Banana release. Both prerender and
  // runtime registration use this function; legacy article graphs stay frozen.
  if (route === "/blog/nano-banana-google-search-button/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    if (article && breadcrumb) {
      // Verified historical JPEG dimensions; preserve the URL and other image metadata.
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 1320, height: 766 };
      }
      // The shared content-field counter excludes this article's rendered FAQ answers.
      delete article.wordCount;
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: article.headline,
        description: article.description,
        inLanguage: article.inLanguage,
        datePublished: article.datePublished,
        dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website },
        mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] },
        primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Exact AI Overviews refresh: finish both producers in the existing graph.
  // Keep publisher ProfessionalService; prohibit commercial types only on this article.
  if (route === "/blog/track-ai-overview-impressions-google-search-console/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      const disallowed = new Set(["Service", "Product", "Offer", "Review", "AggregateRating", "SoftwareApplication"]);
      if (Array.isArray(article.about)) article.about = article.about.filter((value) =>
        !(value && typeof value === "object" && disallowed.has(String((value as SchemaNode)["@type"]))));
      delete article.speakable;
      delete article.wordCount; // Body-only counter excludes the separate FAQ/commissioning slots.
      article.mentions = [
        { "@type": "Thing", name: "Google Search Console", sameAs: "https://en.wikipedia.org/wiki/Google_Search_Console" },
        { "@type": "Thing", name: "Google Analytics", sameAs: "https://en.wikipedia.org/wiki/Google_Analytics" },
      ];
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 1200, height: 800 };
      }
      if (article.hasPart && typeof article.hasPart === "object" && !Array.isArray(article.hasPart)) {
        article.hasPart = { ...(article.hasPart as SchemaNode), name: "Quick Summary", description: "Article quick summary" };
      }
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: "en-US",
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Exact Expedia case analysis: the same target-only closure applies in SSR and runtime.
  if (route === "/blog/expedia-ai-disintermediation-agentic-commerce-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      const disallowed = new Set(["Service", "Product", "Offer", "Review", "AggregateRating", "FinancialService"]);
      if (Array.isArray(article.about)) article.about = article.about.filter((value) =>
        !(value && typeof value === "object" && disallowed.has(String((value as SchemaNode)["@type"]))));
      article["@type"] = "BlogPosting";
      delete article.speakable;
      delete article.wordCount; // Body-only counter excludes the shared FAQ and commissioning slots.
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 2560, height: 1440 };
      }
      if (article.hasPart && typeof article.hasPart === "object" && !Array.isArray(article.hasPart)) {
        article.hasPart = { ...(article.hasPart as SchemaNode), name: "Quick Summary", description: "Article quick summary" };
      }
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: "en-US",
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Item 16 FAQ/schema-only refresh; preserve all other route graphs and visible fee claims.
  if (route === "/blog/best-b2b-agencies-london-by-published-fee/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      delete article.speakable;
      delete article.wordCount; // The inherited body-only counter excludes separately rendered FAQs.
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 2560, height: 1440 };
      }
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
        ...(faq ? { hasPart: { "@id": faq["@id"] } } : {}),
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Exact FAQ-only Manus refresh: define the page already referenced by its Article.
  if (route === "/blog/manus-pro-ai-agent-advanced-capabilities-vs-gemini-claude-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      delete article.speakable;
      delete article.wordCount; // Existing body-only counter excludes the separate visible FAQs.
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
        ...(faq ? { hasPart: { "@id": faq["@id"] } } : {}),
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  if (route === "/services/aeo-agency-london/") {
    const page = completedGraph.find((node) => node["@id"] === "https://integrated.social/services/aeo-agency-london/#webpage");
    // Keep the existing H1/name when static-date fallback declares title first.
    // This corrects graph precedence only, never body text or title metadata.
    if (page) {
      const declaredPage = schema["@graph"].find(node => node["@id"] === page["@id"] && typeof node.headline === "string");
      if (declaredPage) { page.name = declaredPage.headline; page.headline = declaredPage.headline; }
    }
  }
  // Item 19 only: close the existing editorial graph without asserting a security finding.
  if (route === "/blog/openai-openads-optimizer-testflight-warning-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      article["@type"] = "BlogPosting";
      delete article.speakable;
      delete article.wordCount; // The legacy body-only counter excludes the shared FAQ answers.
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // November 4 benchmark correction: close this route only in SSR and runtime.
  if (route === "/blog/ai-visibility-benchmark-2026-citation-rates-share-of-voice/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      article["@type"] = "BlogPosting";
      delete article.speakable;
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
        ...(faq ? { hasPart: { "@id": faq["@id"] } } : {}),
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Winning page 11 only: describe the preserved article's distinct entities.
  if (route === "/blog/gulf-sovereign-ai-series-humain-g42-helix-qai-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      delete article.speakable;
      delete article.wordCount; // Body-only counter excludes the shared visible FAQ answers.
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 2560, height: 1440 };
      }
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Winning page 15: complete only the existing Google Flow article graph.
  if (route === "/blog/google-flow-ai-marketing-teams-aeo-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      // Public GET verified the unchanged JPEG as 1920 x 1080.
      if (article.image && typeof article.image === "object" && !Array.isArray(article.image)) {
        article.image = { ...(article.image as SchemaNode), width: 1920, height: 1080 };
      }
      delete article.speakable;
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description,
        inLanguage: article.inLanguage, datePublished: article.datePublished,
        dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website },
        mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] },
        primaryImageOfPage: article.image,
        ...(faq ? { hasPart: { "@id": faq["@id"] } } : {}),
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // Source-qualified Instinct trust analysis: close only this article's graph.
  if (route === "/blog/instinct-personal-agent-trust-alignment-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      // Editorial analysis is not a commercial offer or a security certification.
      const disallowed = new Set(["Service", "Product", "Offer", "Review", "AggregateRating"]);
      if (Array.isArray(article.about)) article.about = article.about.filter((value) =>
        !(value && typeof value === "object" && disallowed.has(String((value as SchemaNode)["@type"]))));
      article["@type"] = "BlogPosting";
      delete article.speakable;
      delete article.wordCount; // The legacy counter excludes visible shared FAQ answers.
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: article.inLanguage,
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
        ...(faq ? { hasPart: { "@id": faq["@id"] } } : {}),
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  // FAQ-only operating-model amendment. The effective article remains the
  // authority for its existing headline, description, media and original date.
  // Finish both SSR and runtime producers only on this canonical route.
  if (route === "/blog/aeo-agency-vs-in-house-vs-hybrid-b2b-decision-guide-2026/") {
    const canonical = `https://integrated.social${route}`;
    const article = completedGraph.find((node) => node["@id"] === `${canonical}#article`);
    const breadcrumb = completedGraph.find((node) => isBreadcrumbList(node));
    const faq = completedGraph.find((node) => node["@id"] === `${canonical}#faq`);
    if (article && breadcrumb) {
      // This editorial comparison does not describe a commercial offer or service.
      // Preserve the existing Thing topics and canonical publisher identities.
      const disallowed = new Set(["Service", "Product", "Offer", "Review", "AggregateRating"]);
      if (Array.isArray(article.about)) article.about = article.about.filter((value) =>
        !(value && typeof value === "object" && disallowed.has(String((value as SchemaNode)["@type"]))));
      delete article.speakable;
      breadcrumb["@id"] = `${canonical}#breadcrumb`;
      article.mainEntityOfPage = { "@id": `${canonical}#webpage` };
      if (faq) faq.isPartOf = { "@id": `${canonical}#webpage` };
      const page: SchemaNode = {
        "@type": "WebPage", "@id": `${canonical}#webpage`, url: canonical,
        name: article.headline, description: article.description, inLanguage: "en-US",
        datePublished: article.datePublished, dateModified: article.dateModified,
        isPartOf: { "@id": SCHEMA_ENTITY_IDS.website }, mainEntity: { "@id": article["@id"] },
        breadcrumb: { "@id": breadcrumb["@id"] }, primaryImageOfPage: article.image,
      };
      const index = completedGraph.findIndex((node) => node["@id"] === page["@id"]);
      if (index === -1) completedGraph.push(page);
      else completedGraph[index] = page;
    }
  }
  return { ...schema, "@graph": completeWinningPage08Schema(completeWinningPage01Graph(completedGraph, route), route) };
}

function stripHowToNodes(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => !(entry && typeof entry === "object" && isEntityType(entry as SchemaNode, "HowTo")))
      .map(stripHowToNodes);
  }
  if (value && typeof value === "object") {
    const node = value as SchemaNode;
    if (String(node["@id"] ?? "").endsWith("#howto")) return undefined;
    return Object.fromEntries(
      Object.entries(node)
        .map(([key, nested]) => [key, stripHowToNodes(nested)] as const)
        .filter(([, nested]) => nested !== undefined),
    );
  }
  return value;
}

/**
 * Serializes the valid JSON-LD blocks for a rendered route as one coherent graph.
 * Route-local core entities are appended once, and non-blog routes omit HowTo markup.
 * Public non-local routes use the head placement so crawlers and post-hydration
 * navigation see one stable graph. Local-area pages retain their separately
 * governed legacy placement until that programme is reviewed independently.
 */
export function completeSchemaGraphsInHtml(
  html: string,
  options: { allowHowTo?: boolean; placement?: "head" | "existing"; route?: string } = {},
): string {
  const pattern = /<script([^>]*\btype=["']application\/ld\+json["'][^>]*)>([\s\S]*?)<\/script>/gi;
  const matches = Array.from(html.matchAll(pattern));
  const parsed = matches.flatMap((match) => {
    try {
      const schema = JSON.parse(match[2]) as SchemaGraph & SchemaNode;
      return Array.isArray(schema["@graph"]) ? schema["@graph"] : [schema as SchemaNode];
    } catch {
      return [];
    }
  });

  if (parsed.length === 0) return html;

  const graphNodes = options.allowHowTo ? parsed : stripHowToNodes(parsed) as SchemaNode[];
  const breadcrumbId = options.route ? `https://integrated.social${options.route}#breadcrumb` : undefined;
  const completed = completeSchemaGraph({ "@context": "https://schema.org", "@graph": normalizeBreadcrumbNodes(graphNodes, breadcrumbId) }, options.route);
  const serialized = JSON.stringify(completed).replace(/</g, "\\u003c");
  const routeAttribute = options.route ? ` data-schema-route="${options.route}"` : "";
  const graphTag = `<script id="integrated-social-schema-graph" type="application/ld+json" data-site-schema="true"${routeAttribute}>${serialized}</script>`;
  let inserted = false;
  const stripSchemaBlocks = html.replace(pattern, (full, _attributes: string, serializedBlock: string) => {
    try {
      JSON.parse(serializedBlock);
    } catch {
      return full;
    }
    if (options.placement === "existing" && !inserted) {
      inserted = true;
      return graphTag;
    }
    return "";
  });

  if (options.placement === "existing") {
    return stripSchemaBlocks;
  }

  return stripSchemaBlocks.replace("</head>", `    ${graphTag}\n  </head>`);
}
